/**
 * Table Explorer / Profile / Designer / Database Meta Info / Schema Overview /
 * Spatial Explorer.
 *
 * Moved verbatim out of `MainToolbar.tsx` by task B11. Table Explorer keeps
 * its `source` discriminator so the primary button and the More ▸ Tables ▸
 * Explorer row stay distinguishable in the usage data the B15 reshuffle is
 * decided from (INJ-2).
 */
import { useEffect, useState } from 'react';
import { addBreadcrumb } from '../../crash-dialog/services/breadcrumbService';
import { useTableExplorerDialogStore } from '../../workspace/table-explorer';
import { useMetaInfoManagerDialogStore } from '../../workspace/common/components/meta-info/metaInfoManagerDialogStore';
import {
  supportsSchemaOverviewEngineFilter,
  supportsTableDesignerEngineFilter,
  supportsSpatialExplorerEngineFilter,
  supportsTableExplorerEngineFilter,
} from '../model/toolbarEngineFilters';
import type { DatabaseEngineId } from '@/core/engines';
import type { ToolbarActionContext } from './toolbarActionContext';

export type ResolvedTableDesignerContext = {
  connectionId: string;
  connectionName: string;
  serverHost: string;
  database?: string;
  engine?: string;
};

export function useToolbarExplorerActions(ctx: ToolbarActionContext) {
  const { track, requestContext, addTab, activeConnections, validDbSelection } = ctx;
  const openTableExplorerDialog = useTableExplorerDialogStore((s) => s.openDialog);

  const [showTableDesignerDialog, setShowTableDesignerDialog] = useState(false);
  const [resolvedTableDesignerContext, setResolvedTableDesignerContext] =
    useState<ResolvedTableDesignerContext | null>(null);

  // Close the Table Designer dialog when connection or database changes.
  useEffect(() => {
    setShowTableDesignerDialog(false);
  }, [validDbSelection?.connectionId, validDbSelection?.database]);

  const handleOpenTableExplorer = async (source: 'toolbar' | 'more') => {
    addBreadcrumb('click', 'toolbar:table-explorer');
    track('toolbar:table_explorer', { source });
    // Try to resolve context (prompts user if needed). Same predicate as the
    // button's disabled gate (S1 Task 1.16) — the picker never offers, and the
    // action never opens, a connection with no Table Explorer.
    const context = await requestContext(
      { requireConnection: true, requireDatabase: true, engineFilter: supportsTableExplorerEngineFilter },
      'Table Explorer'
    );

    // If user cancelled, do nothing
    if (!context || !context.database) return;

    // Open the global Table Explorer dialog
    const activeConnection = activeConnections.find((c) => c.id === context.connectionId);
    const engineId = (context.engine || activeConnection?.connection.engine || 'mssql') as DatabaseEngineId;
    // Re-validated after the async pick: the selection can have changed while
    // the selector was open.
    if (!supportsTableExplorerEngineFilter(engineId)) return;

    openTableExplorerDialog({
      connectionId: context.connectionId,
      connectionName: context.connectionName,
      serverHost: context.serverHost,
      database: context.database,
      engineId,
    });
  };

  const handleOpenProfile = async () => {
    addBreadcrumb('click', 'toolbar:data-profile');
    track('toolbar:data_profile');
    const context = await requestContext(
      { requireConnection: true, requireDatabase: true },
      'Profile Data'
    );
    if (!context || !context.database) return;
    const activeConnection = activeConnections.find((c) => c.id === context.connectionId);
    const engineId = (context.engine || activeConnection?.connection.engine || 'mssql') as DatabaseEngineId;
    openTableExplorerDialog({
      connectionId: context.connectionId,
      connectionName: context.connectionName,
      serverHost: context.serverHost,
      database: context.database,
      engineId,
      purpose: 'open-profile',
    });
  };

  const handleOpenTableDesigner = async () => {
    addBreadcrumb('click', 'toolbar:table-designer');
    track('toolbar:table_designer');
    // Try to resolve context (prompts user if needed)
    // UI review F4: the same capability gate as the home card's Designer (no Table Designer for Kusto / Azure Storage).
    const context = await requestContext(
      { requireConnection: true, requireDatabase: true, engineFilter: supportsTableDesignerEngineFilter },
      'Table Designer'
    );

    // If user cancelled, do nothing
    if (!context || !context.database) return;

    // Store the resolved context for the dialog to use
    setResolvedTableDesignerContext(context);
    setShowTableDesignerDialog(true);
  };

  const handleOpenMetaInfoManager = async () => {
    addBreadcrumb('click', 'toolbar:meta-info-manager');
    const context = await requestContext(
      { requireConnection: true, requireDatabase: true },
      'Database Meta Info'
    );
    if (!context || !context.database) return;
    track('feature:metainfo_manager_open', { source: 'main_toolbar_more' });
    useMetaInfoManagerDialogStore.getState().openDialog({
      connectionId: context.connectionId,
      database: context.database,
    });
  };

  const handleOpenSchemaOverview = async () => {
    addBreadcrumb('click', 'toolbar:schema-overview');
    track('toolbar:schema_overview');
    // Try to resolve context (prompts user if needed)
    const context = await requestContext(
      { requireConnection: true, requireDatabase: true, engineFilter: supportsSchemaOverviewEngineFilter },
      'Schema Overview'
    );

    // If user cancelled, do nothing
    if (!context || !context.database) return;

    addTab({
      title: `Overview: ${context.database}`,
      type: 'schema-overview',
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: context.connectionName,
        serverHost: context.serverHost,
        database: context.database,
      },
      content: {
        connectionId: context.connectionId,
        databaseName: context.database,
        filterMode: 'all',
        isLoading: true,
      },
    });
  };

  const handleOpenSpatialExplorer = async () => {
    addBreadcrumb('click', 'toolbar:spatial-explorer');
    track('toolbar:spatial_explorer');
    // Try to resolve context (prompts user if needed)
    const context = await requestContext(
      { requireConnection: true, requireDatabase: true, engineFilter: supportsSpatialExplorerEngineFilter },
      'Spatial Explorer'
    );

    // If user cancelled, do nothing
    if (!context || !context.database) return;

    addTab({
      title: `Map: ${context.database}`,
      type: 'spatial-explorer',
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: context.connectionName,
        serverHost: context.serverHost,
        database: context.database,
      },
      content: {
        connectionId: context.connectionId,
        databaseName: context.database,
        view: { layers: [], basemap: 'default' },
      },
    });
  };

  return {
    tableDesignerDialog: {
      open: showTableDesignerDialog,
      setOpen: setShowTableDesignerDialog,
      resolvedContext: resolvedTableDesignerContext,
      setResolvedContext: setResolvedTableDesignerContext,
    },
    actions: {
      'table-explorer': () => void handleOpenTableExplorer('toolbar'),
      'table-explorer-more': () => void handleOpenTableExplorer('more'),
      'data-profile': () => void handleOpenProfile(),
      'table-designer': () => void handleOpenTableDesigner(),
      'meta-info-manager': () => void handleOpenMetaInfoManager(),
      'schema-overview': () => void handleOpenSchemaOverview(),
      'spatial-explorer': () => void handleOpenSpatialExplorer(),
    },
  };
}
